import { createFileRoute, Link } from "@tanstack/react-router";
import { getBusinessConfig } from "@/lib/site-config";
import { seoHead } from "@/lib/seo";
import { FAQAccordion, faqSchema } from "@/components/FAQAccordion";

const PRIVACY_FAQS = [
  {
    q: "What personal data does Woodseer Autos collect?",
    a: "Only what's needed to give you a quote, book your MOT/service or follow up after a repair: your name, contact details (phone, email, optional WhatsApp), vehicle make/model/registration, and a short description of the work needed. We never collect financial details on this website — payment is taken in person at the workshop.",
  },
  {
    q: "How long do you keep my data?",
    a: "Customer records (name, contact, vehicle history) are kept for 7 years to meet UK accounting and DVSA record-keeping rules. Quote-form enquiries that don't turn into bookings are deleted within 90 days. You can ask us to delete your record sooner — see 'Your rights' below.",
  },
  {
    q: "Do you share my data with third parties?",
    a: "No, we never sell or rent your data. We only share data with HMRC, DVSA or the parts supplier who needs to fulfil an order — and only what's strictly required. Embedded Google Maps on our contact page is governed by Google's own privacy policy.",
  },
  {
    q: "Do you use cookies or analytics?",
    a: "We use a small number of essential cookies to remember your consent choice and any in-progress booking form. With your consent we use privacy-respecting analytics so we can understand which pages are useful — analytics is anonymised and never used for ad targeting. You can decline non-essential cookies via the banner shown on your first visit.",
  },
  {
    q: "How do I see, correct or delete my data?",
    a: "Email us with the subject line 'Data request' and we'll respond within 30 days. We may need to verify your identity (for example, by quoting your vehicle registration) before releasing or deleting records.",
  },
  {
    q: "How can I make a complaint about how my data is handled?",
    a: "Contact us first — we take privacy seriously and will try to resolve any issue. If you're not satisfied, you can complain directly to the UK Information Commissioner's Office (ICO) at ico.org.uk.",
  },
];

export const Route = createFileRoute("/privacy")({
  head: () =>
    seoHead({
      path: "/privacy",
      title: "Privacy Policy | How Woodseer Autos Handles Your Data | E14",
      description:
        "How Woodseer Autos collects, uses and protects your personal data when you use our website, request a quote or bring your vehicle in for MOT, servicing or repairs.",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Privacy Policy", path: "/privacy" },
      ],
      jsonLd: [faqSchema(PRIVACY_FAQS) as Record<string, unknown>],
    }),
  component: PrivacyPage,
});

function PrivacyPage() {
  const biz = getBusinessConfig();

  return (
    <>
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-3xl px-4">
          <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-primary">Legal</p>
          <h1 className="mt-3 text-3xl md:text-4xl font-bold text-foreground tracking-tight">Privacy Policy</h1>
          <p className="mt-2 text-sm text-muted-foreground">Last updated: May 2026 · Reviewed every 12 months.</p>
          <p className="mt-6 text-sm text-foreground/85 leading-relaxed">
            We keep this policy short and plain-English. {biz.name} is an independent garage based at {biz.address}, {biz.postcode}. We're the data controller for any personal information you give us through this website, by phone, by email or in person at the workshop. If anything below is unclear, just ask — call <a href={biz.phoneHref} className="text-primary font-semibold hover:underline">{biz.phone}</a> or email <a href={`mailto:${biz.email}`} className="text-primary font-semibold hover:underline">{biz.email}</a>.
          </p>

          <div className="mt-10 space-y-8 text-sm text-muted-foreground leading-relaxed">
            <div>
              <h2 className="text-lg font-bold text-foreground mb-2">1. Who we are</h2>
              <p>{biz.name} ("we", "us") is an independent, family-run garage and DVSA-authorised MOT testing station (V106376) operating from {biz.address}, {biz.postcode}. Yard entrance via {biz.entrance.split(" — ")[0]}.</p>
            </div>

            <div>
              <h2 className="text-lg font-bold text-foreground mb-2">2. What we collect and why</h2>
              <ul className="mt-2 list-disc pl-5 space-y-1.5">
                <li><strong className="text-foreground">Contact details</strong> (name, phone, email, optional WhatsApp number) — so we can quote, confirm bookings and call you when work is finished.</li>
                <li><strong className="text-foreground">Vehicle details</strong> (registration, make, model, mileage, service history) — so we can quote accurately and keep your service record correct for warranty and future MOTs.</li>
                <li><strong className="text-foreground">Job notes</strong> (the symptoms or work you've requested) — so we can prep parts and time before you arrive.</li>
                <li><strong className="text-foreground">Website analytics</strong> (anonymous page views, device/browser type) — only with your consent, never used for ad targeting.</li>
              </ul>
              <p className="mt-3">We do not store payment card details on this website — all payments are taken in person at the workshop.</p>
            </div>

            <div>
              <h2 className="text-lg font-bold text-foreground mb-2">3. Lawful basis</h2>
              <p>Under UK GDPR we rely on three lawful bases: <strong className="text-foreground">contract</strong> (to perform the MOT/service/repair you've asked for), <strong className="text-foreground">legal obligation</strong> (HMRC and DVSA record-keeping for 7 years), and <strong className="text-foreground">legitimate interest</strong> (replying to your enquiry, improving the website). Marketing-style emails are sent only with your express consent and you can opt out at any time.</p>
            </div>

            <div>
              <h2 className="text-lg font-bold text-foreground mb-2">4. How long we keep it</h2>
              <p>Customer records are kept for 7 years to satisfy UK tax and DVSA testing requirements. Quote-form enquiries that don't turn into bookings are deleted within 90 days. Anonymous analytics is rolled up after 14 months.</p>
            </div>

            <div>
              <h2 className="text-lg font-bold text-foreground mb-2">5. Who we share with</h2>
              <p>We do not sell or rent your data. We only share data when strictly necessary: with HMRC and DVSA (legal obligation), with the parts supplier we need to order from (for example, quoting a brake disc against your VIN), and with our hosting and email providers who process data on our instructions under written agreement.</p>
            </div>

            <div>
              <h2 className="text-lg font-bold text-foreground mb-2">6. Cookies and analytics</h2>
              <p>This site uses a small number of essential cookies (consent choice, in-progress booking form). With your consent we use privacy-respecting analytics so we can see which pages help drivers and which don't. The Google Map embedded on the contact page is governed by Google's own privacy policy. You can change your cookie choice at any time via the banner shown on your first visit, or by clearing site data in your browser.</p>
            </div>

            <div>
              <h2 className="text-lg font-bold text-foreground mb-2">7. Your rights</h2>
              <p>Under UK GDPR you have the right to: access the data we hold on you, ask us to correct it, ask us to delete it (subject to our 7-year DVSA/HMRC obligations), object to processing, and complain to the ICO. To make a request, email <a href={`mailto:${biz.email}`} className="text-primary font-semibold hover:underline">{biz.email}</a> with the subject "Data request". We respond within 30 days.</p>
            </div>

            <div>
              <h2 className="text-lg font-bold text-foreground mb-2">8. Security</h2>
              <p>Customer records are stored on UK-hosted, encrypted systems with access limited to staff who need it. Paper job-cards are kept in the workshop office and securely destroyed when no longer required.</p>
            </div>

            <div>
              <h2 className="text-lg font-bold text-foreground mb-2">9. Updates to this policy</h2>
              <p>We review this policy at least every 12 months. The "last updated" date at the top reflects the most recent change. Material changes are highlighted on this page for at least 30 days.</p>
            </div>

            <div>
              <h2 className="text-lg font-bold text-foreground mb-2">10. Contact</h2>
              <p>Questions? Call <a href={biz.phoneHref} className="text-primary font-semibold hover:underline">{biz.phone}</a> or email <a href={`mailto:${biz.email}`} className="text-primary font-semibold hover:underline">{biz.email}</a>. See our <Link to="/terms" className="text-primary font-semibold hover:underline">Terms & Conditions</Link> and <Link to="/contact" className="text-primary font-semibold hover:underline">Contact page</Link> for more.</p>
            </div>
          </div>
        </div>
      </section>

      <FAQAccordion faqs={PRIVACY_FAQS} title="Privacy FAQs" />
    </>
  );
}
